/**
 * The two configured origins of the chrome and the link projection over them (kernel apps, WS-13
 * section 3.1, R-013).
 *
 * The chrome never derives one host from another. On an org-assigned host, `kernel.` plus the
 * document host names a host with no DNS record and no certificate (DES-7). So each host hands
 * the chrome both origins from its own configuration:
 * - the shell (WS-16): `self: 'shell'`, `shell` is `location.origin`, `kernel` is the
 *   `window.__ZION_KERNEL_ORIGIN__` value that WS-5 renders (R-013);
 * - the kernel SPA (WS-15): `self: 'kernel'`, `kernel` is `location.origin`, `shell` comes from
 *   its own configuration.
 * This module holds no host rule. Every function here is pure.
 */
import { isNavigablePath } from './paths.js';

/** Which of the two origins a document or a destination is on. */
export type ChromeOriginKind = 'shell' | 'kernel';

/**
 * The two origins of one data plane, and which of them the current document is on. Each origin is
 * a serialised origin: a scheme, a host and an optional port, with no path and no trailing slash.
 */
export interface ChromeOrigins {
  readonly self: ChromeOriginKind;
  readonly shell: string;
  readonly kernel: string;
}

/**
 * Why {@link checkChromeOrigins} refused a configuration:
 * - `origin-invalid`: a value is not a string, `new URL` refuses it, or its `url.origin` differs
 *   from the value (a path, a user, a trailing slash, an upper-case host);
 * - `insecure-scheme`: a scheme is other than `https:`;
 * - `origins-equal`: the two origins are one origin;
 * - `self-mismatch`: the origin that `self` names differs from the document origin, or `self` is
 *   neither `shell` nor `kernel`.
 */
export type ChromeOriginRefusal = 'origin-invalid' | 'insecure-scheme' | 'origins-equal' | 'self-mismatch';

/**
 * The refusal of {@link checkChromeOrigins}. The message is the reason alone and holds no origin
 * text, so a fatal screen or a log line that shows it leaks no configuration. A host tells the
 * error apart by `name` and reads `reason`.
 */
export class ChromeOriginError extends Error {
  override readonly name = 'ChromeOriginError' as const;
  readonly reason: ChromeOriginRefusal;

  constructor(reason: ChromeOriginRefusal) {
    super(reason);
    this.reason = reason;
  }
}

/** True when `value` is a string whose parsed origin is the value itself. */
function isSerialisedOrigin(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  return url.origin === value;
}

/**
 * Check the two configured origins of a host before the chrome uses them.
 *
 * Callers: the shell's boot (WS-16) and the kernel SPA's entry (WS-15), once per document, with
 * `location.origin` as `documentOrigin`. A refusal ends the boot with the host's fatal screen.
 *
 * The four checks run in this order, and the first that fails throws {@link ChromeOriginError}
 * with its reason. Each check reads `shell` first, then `kernel`.
 * 1. `origin-invalid`: each value is a serialised origin.
 * 2. `insecure-scheme`: each scheme is `https:` (refuse over accept, defaults row 3). A loopback
 *    host gets no exception.
 * 3. `origins-equal`: `shell` differs from `kernel`, because decision 2 needs two origins.
 * 4. `self-mismatch`: the origin that `self` names equals `documentOrigin`, so a `route` answer of
 *    {@link chromeHref} stays on the document.
 *
 * @returns A new frozen {@link ChromeOrigins} with the three input values. The input stays as it
 *   was. The function has no side effect.
 */
export function checkChromeOrigins(configured: ChromeOrigins, documentOrigin: string): ChromeOrigins {
  const { self, shell, kernel } = configured;
  if (!isSerialisedOrigin(shell) || !isSerialisedOrigin(kernel)) throw new ChromeOriginError('origin-invalid');
  if (new URL(shell).protocol !== 'https:' || new URL(kernel).protocol !== 'https:') {
    throw new ChromeOriginError('insecure-scheme');
  }
  if (shell === kernel) throw new ChromeOriginError('origins-equal');
  const named = self === 'shell' ? shell : self === 'kernel' ? kernel : null;
  if (named !== documentOrigin) throw new ChromeOriginError('self-mismatch');
  return Object.freeze({ self, shell, kernel });
}

/** Where a chrome link goes: an origin and an absolute path on it. */
export interface ChromeDestination {
  readonly origin: ChromeOriginKind;
  readonly path: string;
}

/**
 * The link that {@link chromeHref} builds:
 * - `route`: a same-origin path, for the host's router;
 * - `document`: an absolute URL on the other origin, for a top-level navigation.
 */
export type ChromeHref = { readonly mode: 'route'; readonly to: string } | { readonly mode: 'document'; readonly href: string };

/**
 * Project a destination onto the two origins.
 *
 * Callers: `ChromeLink` (WS-13b) for every header tile, brand link, Profile, Settings and sidebar
 * item. The destination's path comes from registry rows and nav items, which a publisher wrote,
 * so the function treats it as untrusted.
 *
 * @param origins The output of {@link checkChromeOrigins}.
 * @param destination The origin and the path to reach.
 * @returns
 * - `null` when `destination.path` fails `isNavigablePath`;
 * - `null` when `destination.origin` is neither `shell` nor `kernel`;
 * - `{ mode: 'route', to: path }` when `destination.origin` is `origins.self`;
 * - otherwise the path resolved against that origin: `{ mode: 'document', href }` when the
 *   resolved origin equals the configured origin, and `null` when it differs or the configured
 *   origin does not parse.
 *
 * It adds no query, fragment or token, so the shell hands the kernel origin a path and nothing
 * else. It never throws and has no side effect.
 */
export function chromeHref(origins: ChromeOrigins, destination: ChromeDestination): ChromeHref | null {
  const { origin, path } = destination;
  if (!isNavigablePath(path)) return null;
  if (origin !== 'shell' && origin !== 'kernel') return null;
  if (origin === origins.self) return { mode: 'route', to: path };
  const base = origin === 'kernel' ? origins.kernel : origins.shell;
  let url: URL;
  try {
    url = new URL(path, base);
  } catch {
    return null;
  }
  if (url.origin !== base) return null;
  return { mode: 'document', href: url.href };
}
